/**
 * @file   src\pages\MyFavorites.js
 * @brief  This file is responsible for my favourites listing.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Form from "react-bootstrap/Form";
import MyWantadMap from "../images/my-wantad-map.png";
import StaticMapboxComponent from "../components/Common/StaticMapboxComponent";
import usePolygons from "../hooks/usePolygons";
import {
  parseDrawnPolygonWKT,
  normalizeZipCodes,
  hasUsableCoordinates,
} from "../helpers/mapbox";
import ShareIconSmall from "../images/share-icon-small.svg";
import FavoriteIconSmall from "../images/favorite-icon-small.svg";
import FavoritesFilterUp from "../images/favorites-filter-icon-up.svg";
import FavoritesFilterDown from "../images/favorites-filter-icon-down.svg";
import { useNavigate, useLocation } from "react-router-dom";
import BedIcon from "../images/bed-icon.svg";
import BathIcon from "../images/bath-icon.svg";
import SqftIcon from "../images/sqft-icon.svg";
import MoneyIcon from "../images/money-icon.svg";
import { LOT_LAND, USER_INFO_KEY, USER_TYPE_AGENT, WANT_AD_KEY } from "../constants/common";
import {
  convertToCurrencySystem,
  getLocalStorage,
  getUserType,
  locationDisplayInListing,
  setLocalStorage,
} from "../helpers/common";
import {
  getFavouriteWantAdList,
  favouriteWantAd,
  unFavouriteWantAd,
  getWantAdById,
} from "../services/wantAdService";
import { toast } from "react-toastify";
import LoadingSkeleton from "./LoadingSkeleton";
import { pageURLs } from "@/constants";
import SelectDropDown from "../components/Common/SelectDropDown";
import { getPropertyIcon, getCombinedText } from "../helpers/getPropertyIcons";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { wantAdSqftValue } from "../helpers/createWantAd";
import ShareYourAdModal from "../components/MemberWantAd/v2/ShareYourAdModal";
import { themeColors } from "../themeColors";
import BackButton from "@/components/Common/BackButton";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

const PAGE_LIMIT = 10;

/**
 * To list favorite want-ad
 *
 * Declared at module scope (not inside MyFavorites) so it's a stable
 * component type across parent re-renders - a function declared inside a
 * render body is a *new* type every render, which makes React unmount and
 * remount every card instead of re-rendering it. That was fine when this was
 * pure markup, but it now holds usePolygons (a data-fetching hook), so a
 * remount storm on every parent re-render (sort change, infinite-scroll
 * fetch) would refire every card's polygon fetch. React.memo skips
 * re-renders when props are unchanged, once the handlers below are stable.
 */
const WantAd = React.memo(function WantAd({
  wantAd,
  renderTooltip,
  onItemClick,
  onShare,
  onFavorite,
}) {
  const wantAdId = _.get(wantAd, "Want_ad_Id", "");
  const PropertyTypeId = _.get(wantAd, "PropertyTypeId", "");
  const subPropertyTypeId = _.get(wantAd, "subPropertyTypeId", "");
  const Buy_or_Rent = _.get(wantAd, "Buy_or_Rent", 1);
  let sqFt = wantAdSqftValue(
    _.get(wantAd, "Sqft_mn", ""),
    _.get(wantAd, "Sqft_mx", ""),
  );

  const Longitude = _.get(wantAd, "Longitude", null);
  const Latitude = _.get(wantAd, "Latitude", null);
  const mapRadius = _.get(wantAd, "mapRadius", null);
  const DrawnPolygonText = _.get(wantAd, "DrawnPolygonText", null);
  const hasCoordinates = hasUsableCoordinates(Longitude, Latitude);

  // getFavWantAdList returns bare zip strings, not the {zipCode, zipId}
  // shape usePolygons/the feed endpoints use - normalize before passing in.
  // Keyed on wantAdId (stable for the lifetime of a mounted card) rather
  // than the raw zipCode value: when a want-ad has no zip codes, the API
  // returns null, and `wantAd.zipCode ?? []` would construct a brand new
  // array every render, which as a usePolygons/useEffect dependency would
  // refire its fetch forever.
  const zipCode = useMemo(
    () => normalizeZipCodes(_.get(wantAd, "zipCode", null)),
    [wantAdId],
  );

  const { polygonCoordinates } = usePolygons(zipCode, !hasCoordinates);

  const drawnPolygonCoords = useMemo(
    () => parseDrawnPolygonWKT(DrawnPolygonText),
    [wantAdId],
  );

  return (
    <div className="col-md-4">
      <a
        href={void 0}
        className="txt-no-underline link-cursor"
        onClick={() => onItemClick(wantAdId)}
      >
        <div className="my-wantad-display mb-0">
          <div className="my-wantad-map h-164">
            <div className="modal-property-tile tile-loation">
              <OverlayTrigger
                placement="bottom-start"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip(
                  PropertyTypeId,
                  subPropertyTypeId,
                  Buy_or_Rent,
                )}
              >
                <div className="property-tile w-auto">
                  {subPropertyTypeId &&
                    subPropertyTypeId.length > 0 &&
                    subPropertyTypeId.map((subtype) => {
                      return (
                        <img
                          src={getPropertyIcon(subtype)}
                          alt={getPropertyIcon(subtype)}
                          className="property-icon-img"
                        />
                      );
                    })}
                </div>
              </OverlayTrigger>
            </div>
            {hasCoordinates ? (
              <StaticMapboxComponent
                width={400}
                height={164}
                center={[Longitude, Latitude]}
                radiusMiles={drawnPolygonCoords ? null : mapRadius}
                polygonCoordinates={drawnPolygonCoords || polygonCoordinates}
                showPolygon={
                  !!drawnPolygonCoords ||
                  (Array.isArray(polygonCoordinates) &&
                    polygonCoordinates.length > 0 &&
                    Array.isArray(zipCode) &&
                    zipCode.length > 0)
                }
              />
            ) : (
              <img src={MyWantadMap} className="wantad-map-img" alt="" />
            )}
          </div>
          <div className="my-wantad-details">
            <div className="my-wantad-details-top d-flex justify-content-between">
              <div>
                <div className="want-ad-tag mb-2 grey-tag-bg align-top">
                  <span className="tag-icon grey-color-icon text-uppercase me-0">
                    {wantAd.Buy_or_Rent == 1 ? "Buy" : "Rent"}
                  </span>
                </div>
                <h2>{wantAd?.Title_Text}</h2>
                <h6>{locationDisplayInListing(wantAd)}</h6>
              </div>
              <div className="wantad-sidenav-wrap">
                <a
                  role="button"
                  className="social-action-btn mr-12"
                  onClick={(e) => onShare(e, wantAd)}
                >
                  <img src={ShareIconSmall} alt="" />
                </a>
                <a
                  role="button"
                  className="social-action-btn"
                  onClick={(e) => {
                    onFavorite(true, wantAd?.Want_ad_Id, wantAd?.ClientId, e);
                  }}
                >
                  <img src={FavoriteIconSmall} alt="" />
                </a>
              </div>
            </div>
            <ul className="mt-2">
              {wantAd.Bedrooms ? (
                <li>
                  <div className="list-icns">
                    <img src={BedIcon} alt="" />
                  </div>
                  <span className="list-txt">
                    {wantAd.Bedrooms &&
                      wantAd.Bedrooms + (wantAd.Bedrooms == "any" ? "" : "+")}
                  </span>
                </li>
              ) : (
                ""
              )}
              {wantAd.Bathrooms ? (
                <li>
                  <div className="list-icns">
                    <img src={BathIcon} alt="" />
                  </div>
                  <span className="list-txt">
                    {wantAd.Bathrooms &&
                      wantAd.Bathrooms +
                      (wantAd.Bathrooms == "any" ? "" : "+")}
                  </span>
                </li>
              ) : (
                ""
              )}
              {sqFt ? (
                <li>
                  <div className="list-icns">
                    <img src={SqftIcon} alt="" />
                  </div>
                  <span className="list-txt">{sqFt} sqft</span>
                </li>
              ) : (
                ""
              )}
              {wantAd?.Budget_mn || wantAd?.Budget_mx ? (
                <li>
                  <div className="list-icns">
                    <img src={MoneyIcon} alt="" />
                  </div>
                  <span className="list-txt">
                    {wantAd.Budget_mn &&
                      "$" + convertToCurrencySystem(wantAd.Budget_mn)}{" "}
                    -{" "}
                    {wantAd.Budget_mx &&
                      convertToCurrencySystem(wantAd.Budget_mx)}{" "}
                    {wantAd?.Buy_or_Rent == 2 && "/month"}{" "}
                    {wantAd?.Buy_or_Rent == 3 && "/night"}
                  </span>
                </li>
              ) : (
                ""
              )}
            </ul>
          </div>
        </div>
      </a>
    </div>
  );
});

const MyFavorites = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const sessionData = getLocalStorage(USER_INFO_KEY);
  const clientId = location.state ? location.state["clientId"] : null;

  const [sortBy, setSortBy] = useState("recently favorited");
  const [sortOrder, setSortOrder] = useState(true);
  const [favoriteCount, setFavoriteCount] = useState(
    location.state && location.state["count"] ? location.state["count"] : 0,
  );
  const [showShareModal, setShareModal] = useState(false);
  const handleClose = () => setShareModal(false);
  const [selectedWantAd, setSelectedWantAd] = useState({});
  const toastId = useRef(null);

  const options = [
    { value: "recently favorited", label: "Recently Favorited" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "price", label: "Price" },
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["favouriteWantAds", clientId, sortBy, sortOrder],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getFavouriteWantAdList({
        clientId,
        sortBy,
        sortDir: sortOrder ? "asc" : "desc",
        page: pageParam,
        limit: PAGE_LIMIT,
      });
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      const results = _.get(lastPage, "result", []);
      if (!results || results.length < PAGE_LIMIT) return undefined;
      return allPages.length + 1;
    },
    select: (data) => ({
      ...data,
      wantAdList: data.pages.flatMap((page) => _.get(page, "result", [])),
    }),
  });

  const wantAdList = data?.wantAdList ?? [];
  const showNoResult = !isLoading && wantAdList.length === 0;

  // Infinite scroll listener
  useEffect(() => {
    const handleScroll = () => {
      const userScrollHeight = window.innerHeight + window.scrollY;
      const windowBottomHeight = document.documentElement.offsetHeight;
      if (userScrollHeight >= windowBottomHeight && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Mutation for share modal - fetches want ad details on demand
  const shareWantAdMutation = useMutation({
    mutationFn: (wantAdId) => getWantAdById(wantAdId),
    onSuccess: (response) => {
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        setSelectedWantAd(responseResult);
        setShareModal(true);
      }
    },
  });

  // Mutation for favorite/unfavorite with cache invalidation
  const favoriteMutation = useMutation({
    mutationFn: ({ isFavourite, data }) =>
      isFavourite ? unFavouriteWantAd(data) : favouriteWantAd(data),
    onSuccess: (response, { isFavourite, wantAdId, clientId: wantAdClientId }) => {
      if (response.errorCode === 0) {
        toast.dismiss(toastId.current);
        toast.success(MyMsg(wantAdId, isFavourite, wantAdClientId), {
          toastId: response.result.wantAdId,
        });
        setFavoriteCount((prev) => (isFavourite ? prev - 1 : prev + 1));
        queryClient.invalidateQueries({ queryKey: ["favouriteWantAds"] });
      }
    },
  });

  /**
   * Toast message
   */
  const MyMsg = (Want_ad_Id, IsFavourite, clientId) => {
    const message = IsFavourite
      ? "Want-Listing has been removed from your favorites"
      : "Want-Listing has been added to your favorites";
    return (
      <div className="d-flex align-items-center justify-content-between">
        <span className="d-flex flex-grow-1">{message}</span>
        <button
          className="btn-primary text-nowrap me-2"
          onClick={(e) =>
            handleFavoriteWantAd(!IsFavourite, Want_ad_Id, clientId, e)
          }
        >
          {" "}
          Undo
        </button>
      </div>
    );
  };

  /**
   * To handle the want-ad favorite
   */
  const handleFavoriteWantAd = useCallback(
    (IsFavourite, Want_ad_Id, clientId, e) => {
      e.stopPropagation();
      if (!Want_ad_Id) return;

      favoriteMutation.mutate({
        isFavourite: IsFavourite,
        wantAdId: Want_ad_Id,
        clientId,
        data: {
          userId: sessionData.userInfo.userId,
          clientId: clientId ? [clientId] : IsFavourite === false ? [] : null,
          wantAdId: Want_ad_Id,
        },
      });
    },
    [favoriteMutation, sessionData],
  );

  /**
   * To handle navigation to want ad details page
   */
  const handleWantAdItemClick = useCallback(
    (wantAdId) => {
      setLocalStorage(WANT_AD_KEY, {
        wantAdId: wantAdId,
        isFromHomeFeed: true,
      });
      navigate(`${pageURLs.wantAdDetails}/${wantAdId}`);
    },
    [navigate],
  );

  const renderTooltip = useCallback(
    (PropertyTypeId, subPropertyTypeId, Buy_or_Rent) => (
      <Tooltip
        id="button-tooltip"
        placement="bottom-start"
        className="custom-tooltip"
        {...props}
      >
        {subPropertyTypeId &&
          subPropertyTypeId.length > 0 &&
          getCombinedText(subPropertyTypeId)}
      </Tooltip>
    ),
    [props],
  );

  const handleShare = useCallback(
    (e, wantAd) => {
      e.stopPropagation();
      const wantAdId = wantAd?.Want_ad_Id;
      if (wantAdId) {
        shareWantAdMutation.mutate(wantAdId);
      }
    },
    [shareWantAdMutation],
  );

  /**
   * To handle filter
   */
  const handleSortChange = (selectedParam) => {
    setSortBy(selectedParam.value);
  };

  /**
   * To handle sort icon
   */
  const handleSortOrderChange = () => {
    setSortOrder(!sortOrder);
  };

  useMLSAgentAtGitchaHomeRedirect();

  const hasFavoritesData = wantAdList.length > 0;

  return (
    <>
      <PageWrapper>
        <div className="m-0 pb-4">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-3">
              <div className="col-md-12">
                {location.state && location.state["name"] && (
                  <BackButton className="d-inline-block mb-4" text="Back to favorites" onClick={() => navigate("/favorite-clients")} />
                )}
                <div className="my-want-ad-wrap">
                  <div className="top-sec mb-24 md-mt-24">
                    <div className="row m-3">
                      <div className="col">
                        {location.state && location.state["name"] ? (
                          <h5>
                            {location.state["name"]} favorites (
                            {favoriteCount || 0})
                          </h5>
                        ) : (
                          <h5>My Favorites</h5>
                        )}
                      </div>
                      {hasFavoritesData && (
                        <div col="12" className="d-flex d-sm-block col-sm-auto">
                          <div className="d-flex align-items-center">
                            <div className="d-flex select-wrap">
                              <Form.Label className="">Sort by:</Form.Label>
                              <SelectDropDown
                                id="multi3"
                                name="colors3"
                                options={options}
                                className="basic-single-inventory inventory-select mb-0 ms-2"
                                classNamePrefix="select"
                                dropDownIndicatorStatus={false}
                                onChange={(e) => handleSortChange(e)}
                                defaultValue={options[0]}
                                errorClass="error-msg error-msg-height"
                              />
                            </div>
                            <div className="more-info-wrap">
                              <button
                                className="btn sort-btn"
                                onClick={() => handleSortOrderChange()}
                              >
                                {sortOrder == true ? (
                                  <img src={FavoritesFilterUp} alt="" />
                                ) : (
                                  <img src={FavoritesFilterDown} alt="" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="clearfix">
                    <div className="row g-5">
                      {showNoResult ? (
                        <div className="no-result-wrap ">
                          <h3>You don't have any favorited want-listings</h3>
                          <p>
                            When you find a want-listing on the feed you want to
                            favorite, use the Heart icon in the top right
                            corner.
                          </p>
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate("/home")}
                          >
                            Browse Listing Feed
                          </button>
                        </div>
                      ) : (
                        isLoading && <LoadingSkeleton />
                      )}

                      {wantAdList.map((wantAd) => (
                        <WantAd
                          key={_.get(wantAd, "Want_ad_Id")}
                          wantAd={wantAd}
                          renderTooltip={renderTooltip}
                          onItemClick={handleWantAdItemClick}
                          onShare={handleShare}
                          onFavorite={handleFavoriteWantAd}
                        />
                      ))}

                      {isFetchingNextPage && <LoadingSkeleton />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
      {showShareModal && (
        <ShareYourAdModal
          show={showShareModal}
          onHide={handleClose}
          adDetails={selectedWantAd}
          isWantAdCreated={false}
          AdType={_.get(selectedWantAd, "adType", "") === "BUY" ? 1 : 2}
          isAdTypeIsLand={_.get(selectedWantAd, "prprtyType", "") === 6}
          adTypeBasedOnProperty={
            _.get(selectedWantAd, "prprtyType", "") === 6
              ? LOT_LAND
              : _.get(selectedWantAd, "adType", "")
          }
        />
      )}
    </>
  );
};

export default MyFavorites;
